import { isDefined } from "@clipboard-health/util-ts";
import { Avatar as MuiAvatar, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import NoUserIcon from "@src/appV2/redesign/assets/icons-colored/no-user.svg?react";

export const AVATAR_SIZES = ["small", "medium", "large"] as const;
export type AvatarSize = typeof AVATAR_SIZES[number];

export interface AvatarProps {
  alt?: string;
  children?: React.ReactNode;
  size?: AvatarSize;
  src?: string;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  outlineColor?: (theme: Theme) => string;
}

/**
 * Avatar component.
 * For fallback content (broken or missing src) it uses the following logic:
 * 1. If children are provided, use them as fallback.
 * 2. If none provided, use the default fallback icon (NoUserIcon).
 */
export function Avatar(props: AvatarProps) {
  const { alt, sx, size = "medium", children, src, disabled, outlineColor, ...restProps } = props;

  const getFallbackContent = (): React.ReactNode => {
    if (isDefined(children)) {
      return children;
    }

    return <NoUserIcon />;
  };

  return (
    <MuiAvatar
      alt={alt}
      size={size}
      src={src}
      sx={mergeSxProps(
        (theme: Theme) => ({
          opacity: disabled ? 0.6 : undefined,

          ...(isDefined(outlineColor) && {
            outlineColor: outlineColor(theme),
            outlineStyle: "solid",
          }),
        }),
        sx
      )}
      {...restProps}
    >
      {getFallbackContent()}
    </MuiAvatar>
  );
}
